import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeFileUrl } from '@/utils/fileUrl';
import { formatServiceType } from '@/config/serviceTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Pencil, Trash2, Save, X, Loader2, Camera, Upload, 
  Calendar, MapPin, User, Building2, Phone, Mail, FileText, Eye,
  Settings, Sparkles, Clock, History
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { SR_STATUS_CONFIG, WS_STATUS_CONFIG } from '@/config/statusWorkflow';
import { EVENT_TYPES } from '@/config/eventTypes';
import EventTypeCombobox from '@/components/EventTypeCombobox';
import { AlertCircle } from 'lucide-react';

const ServiceRequestDetailPage = () => {
  const { srId } = useParams();
  const navigate = useNavigate();
  const { api, hasRole } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [sr, setSr] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [pendingTransferFacilityId, setPendingTransferFacilityId] = useState(null);

  // FIX 1D & FIX 3: Determine effective status and lockdown state
  const hasWorkOrder = sr?.work_schedule_id || sr?.work_schedule_status;
  const effectiveStatus = hasWorkOrder ? (sr?.work_schedule_status || sr?.status) : sr?.status;
  const isLocked = effectiveStatus === 'CLOSED' || effectiveStatus === 'CANCELLED';
  const canEdit = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']) && !isLocked;

  const fetchAuditHistory = useCallback(async () => {
    try {
      const response = await api.get(`/service-requests/${srId}/audit-history`);
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch audit history:', error);
    }
  }, [api, srId]);

  const [linkedWsBlockingTransfer, setLinkedWsBlockingTransfer] = useState(false);

  const fetchServiceRequest = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/service-requests/${srId}`);
      setSr(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load service request');
      navigate('/service-requests');
    } finally {
      setLoading(false);
    }
  }, [api, srId, navigate]);

  const fetchFacilities = useCallback(async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
    }
  }, [api]);

  // 2026-07-18 — Poll SR transfer eligibility. This calls a dedicated
  // backend endpoint that exactly mirrors the 409 gate on the transfer
  // endpoint (only counts linked WSes at THIS SR's current facility with
  // status IN_PROCESS / ON_HOLD). Previous implementation used the WS list
  // endpoint which ignores `service_request_id` and returned all org-wide
  // active WSes — leading to false blocks on brand-new SRs.
  const fetchLinkedWsBlockingStatus = useCallback(async () => {
    try {
      const res = await api.get(`/service-requests/${srId}/transfer-eligibility`);
      setLinkedWsBlockingTransfer(!res.data?.can_transfer);
    } catch (_) {
      // Best-effort: if we can't fetch, don't block the UI. The backend
      // still enforces the gate on save.
      setLinkedWsBlockingTransfer(false);
    }
  }, [api, srId]);

  useEffect(() => {
    fetchServiceRequest();
    fetchFacilities();
    fetchAuditHistory();
    fetchLinkedWsBlockingStatus();
  }, [srId, fetchServiceRequest, fetchFacilities, fetchAuditHistory, fetchLinkedWsBlockingStatus]);

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const canTransferFacility = hasRole(['ADMIN', 'MANAGER']);
  const isFacilityChanged = !!(editData.facility_id && sr && editData.facility_id !== sr.facility_id);

  const facilityNameById = (fid) => {
    if (!fid) return '—';
    const f = facilities.find(x => x.id === fid);
    return f ? f.name : fid;
  };

  const applyOtherEdits = async () => {
    // PUT all remaining edits (excluding facility_id — that's already
    // been applied by the transfer endpoint, which also reset status).
    const { facility_id, status, ...rest } = editData;
    if (Object.keys(rest).length > 0) {
      await api.put(`/service-requests/${srId}`, rest);
    }
  };

  const doTransfer = async (newFacilityId) => {
    // Atomic backend call: cancels active work, resets status to NEW,
    // stamps transfer metadata, inserts the car-file history event.
    await api.post(`/service-requests/${srId}/transfer-facility`, {
      new_facility_id: newFacilityId,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (isFacilityChanged) {
        // Guard: only Admin/Manager may reach this branch — the UI already
        // hides the dropdown for Operator/Technician, but the backend
        // enforces it either way.
        if (!canTransferFacility) {
          toast.error('You do not have permission to change the Responding Facility.');
          setSaving(false);
          return;
        }
        // Defer the actual work until the user confirms in the modal.
        setPendingTransferFacilityId(editData.facility_id);
        setShowTransferConfirm(true);
        setSaving(false);
        return;
      }

      await api.put(`/service-requests/${srId}`, editData);
      toast.success('Service request updated');
      setIsEditing(false);
      fetchServiceRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update service request');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmTransfer = async () => {
    try {
      setSaving(true);
      await doTransfer(pendingTransferFacilityId);
      // Apply any other pending field edits AFTER the transfer succeeds.
      await applyOtherEdits();
      toast.success('Service Request transferred. Status reset to New.');
      setShowTransferConfirm(false);
      setPendingTransferFacilityId(null);
      setIsEditing(false);
      fetchServiceRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer Service Request');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelTransfer = () => {
    setShowTransferConfirm(false);
    setPendingTransferFacilityId(null);
    // Roll back the facility field in editData so the dropdown snaps back
    // to the currently-persisted value.
    if (sr) {
      setEditData(prev => ({ ...prev, facility_id: sr.facility_id }));
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.delete(`/service-requests/${srId}`);
      toast.success('Service request deleted');
      navigate('/service-requests');
    } catch (error) {
      toast.error('Failed to delete service request');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const customerPhotos = sr?.customer_photos || [];
    if (customerPhotos.length >= 4) {
      toast.error('Maximum 4 photos allowed');
      return;
    }

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/service-requests/${srId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Photo uploaded');
      fetchServiceRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      await api.delete(`/service-requests/${srId}/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchServiceRequest();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const getStatusConfig = (status) => {
    // FIX 3: Use WS_STATUS_CONFIG for work order statuses
    if (WS_STATUS_CONFIG[status]) {
      return WS_STATUS_CONFIG[status];
    }
    return SR_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
      </div>
    );
  }

  if (!sr) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold mb-2">Service Request Not Found</h2>
        <Button onClick={() => navigate('/service-requests')}>Go Back</Button>
      </div>
    );
  }

  // FIX 3: Get status config using effective status (WO status when WO exists)
  const statusConfig = getStatusConfig(effectiveStatus);
  const customerPhotos = sr.customer_photos || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sr-detail-page">
      {/* FIX 1D: Lockdown Banner for Closed/Cancelled */}
      {isLocked && (
        <div className={`p-4 rounded-lg ${effectiveStatus === 'CANCELLED' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="sr-locked-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${effectiveStatus === 'CANCELLED' ? 'text-red-600' : 'text-green-600'}`} />
            <div>
              <p className={`font-medium ${effectiveStatus === 'CANCELLED' ? 'text-red-800' : 'text-green-800'}`}>
                {effectiveStatus === 'CANCELLED' 
                  ? 'This service request is archived. The associated work order was cancelled.'
                  : 'This service request is archived. The associated work order is closed.'
                }
              </p>
              <p className={`text-sm mt-1 ${effectiveStatus === 'CANCELLED' ? 'text-red-700' : 'text-green-700'}`}>
                All data is read-only.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/service-requests')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[#323033]" data-testid="sr-detail-title">
                {sr.car_mark} {sr.car_number}
              </h1>
              {/* FIX 3: Single unified status badge with icon for NEW */}
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`} data-testid="sr-status-badge">
                {statusConfig.icon === 'Sparkles' && <Sparkles className="w-3 h-3" />}
                {statusConfig.label}
              </span>
            </div>
            <p className="text-[#5A5A5A] mt-1" data-testid="sr-detail-subtitle">
              SR #{sr.job_number} • {sr.facility_name || 'Unknown Facility'}
            </p>
          </div>
        </div>
        
        {/* FIX 1D: Hide Edit/Delete buttons when locked */}
        {canEdit && (
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => { setIsEditing(false); setEditData(sr); }} disabled={saving}>
                  <X className="w-4 h-4 mr-2" />Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#005EF8] hover:bg-[#0047C2]">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-sr-btn">
                  <Pencil className="w-4 h-4 mr-2" />Edit
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-sr-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Car Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#005EF8]" />
                Car Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoField label="Car Mark" value={sr.car_mark} editMode={isEditing} field="car_mark" editData={editData} onChange={handleEditChange} />
                <InfoField label="Car Number" value={sr.car_number} editMode={isEditing} field="car_number" editData={editData} onChange={handleEditChange} />
                <InfoField
                  label="Car Type"
                  value={
                    (sr.car_type || '').toUpperCase() === 'OTHER' && sr.car_type_other
                      ? `Other – ${sr.car_type_other}`
                      : sr.car_type
                  }
                  editMode={isEditing}
                  field="car_type"
                  editData={editData}
                  onChange={handleEditChange}
                />
                <InfoField label="Car Owner" value={sr.car_owner} editMode={isEditing} field="car_owner" editData={editData} onChange={handleEditChange} />
                <InfoField label="Car Owner Contact" value={sr.car_owner_contact} editMode={isEditing} field="car_owner_contact" editData={editData} onChange={handleEditChange} icon={<Mail className="w-4 h-4" />} />
                <InfoField label="Car Lessee" value={sr.car_lessee} editMode={isEditing} field="car_lessee" editData={editData} onChange={handleEditChange} />
                <InfoField label="SE Owner" value={sr.se_owner} editMode={isEditing} field="se_owner" editData={editData} onChange={handleEditChange} />
                <InfoField label="Lining Owner" value={sr.lining_owner} editMode={isEditing} field="lining_owner" editData={editData} onChange={handleEditChange} />
                <InfoField label="Lining Type" value={sr.lining_type} editMode={isEditing} field="lining_type" editData={editData} onChange={handleEditChange} />
                <InfoField
                  label="Jacketed"
                  value={typeof sr.jacketed === 'boolean' ? (sr.jacketed ? 'Yes' : 'No') : (sr.jacketed || '-')}
                />
                <InfoField label="Insulation Type" value={sr.insulation_type} editMode={isEditing} field="insulation_type" editData={editData} onChange={handleEditChange} />
                {isEditing ? (
                  <div>
                    <Label>Load Status</Label>
                    <Select
                      value={editData.interior_condition || ''}
                      onValueChange={(v) => handleEditChange('interior_condition', v)}
                    >
                      <SelectTrigger data-testid="load-status-select-edit">
                        <SelectValue placeholder="Select load status" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          { value: 'CLEAN',   label: 'Clean' },
                          { value: 'EMPTY',   label: 'Empty' },
                          { value: 'RESIDUE', label: 'Residue' },
                          { value: 'LOADED',  label: 'Loaded' },
                          { value: 'OTHER',   label: 'Other' },
                        ].map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(editData.interior_condition || '').toUpperCase() === 'OTHER' && (
                      <div className="mt-2">
                        <Label htmlFor="interior_condition_other_edit">Specify Load Status *</Label>
                        <Input
                          id="interior_condition_other_edit"
                          value={editData.interior_condition_other || ''}
                          onChange={(e) => handleEditChange('interior_condition_other', e.target.value)}
                          data-testid="load-status-other-input-edit"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <InfoField
                    label="Load Status"
                    value={(() => {
                      const raw = (sr.interior_condition || '').toUpperCase();
                      if (raw === 'OTHER' && sr.interior_condition_other) return `Other – ${sr.interior_condition_other}`;
                      const LABELS = { CLEAN: 'Clean', EMPTY: 'Empty', RESIDUE: 'Residue', LOADED: 'Loaded', OTHER: 'Other' };
                      return LABELS[raw] || sr.interior_condition;
                    })()}
                  />
                )}
                <InfoField label="Last Contents" value={sr.last_contents} editMode={isEditing} field="last_contents" editData={editData} onChange={handleEditChange} />
              </div>
            </CardContent>
          </Card>

          {/* Scope of Work */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#005EF8]" />
                Scope of Work
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isEditing ? (
                  <div>
                    <Label>Event Type</Label>
                    <EventTypeCombobox
                      options={EVENT_TYPES}
                      value={editData.event_type || ''}
                      onChange={(v) => handleEditChange('event_type', v)}
                      testId="event-type-select-edit"
                      triggerId="event_type_edit"
                    />
                  </div>
                ) : (
                  <InfoField label="Event Type" value={sr.event_type} />
                )}
                <InfoField label="Service Type" value={formatServiceType(sr.service_type)} />
              </div>
              <div>
                <Label className="text-sm text-[#5A5A5A]">Work Procedures</Label>
                {isEditing ? (
                  <Textarea 
                    value={editData.work_procedures || ''} 
                    onChange={(e) => handleEditChange('work_procedures', e.target.value)}
                    rows={3}
                  />
                ) : (
                  <p className="text-[#323033] mt-1 whitespace-pre-wrap">{sr.work_procedures || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-sm text-[#5A5A5A]">Shopping Reason</Label>
                {isEditing ? (
                  <Textarea 
                    value={editData.shopping_reason || ''} 
                    onChange={(e) => handleEditChange('shopping_reason', e.target.value)}
                    rows={3}
                  />
                ) : (
                  <p className="text-[#323033] mt-1 whitespace-pre-wrap">{sr.shopping_reason || '-'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Gasket Specifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#005EF8]" />
                Gasket Specifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoField label="Manway Cover" value={sr.gasket_manway_cover} editMode={isEditing} field="gasket_manway_cover" editData={editData} onChange={handleEditChange} />
                <InfoField label="Flanged Valves" value={sr.gasket_flanged_valves} editMode={isEditing} field="gasket_flanged_valves" editData={editData} onChange={handleEditChange} />
                <InfoField label="BOV Flange" value={sr.gasket_bov_flange} editMode={isEditing} field="gasket_bov_flange" editData={editData} onChange={handleEditChange} />
                <InfoField label="BOV Cap Material" value={sr.gasket_bov_cap_material} editMode={isEditing} field="gasket_bov_cap_material" editData={editData} onChange={handleEditChange} />
                <InfoField label="O-ring Specification" value={sr.gasket_oring_spec} editMode={isEditing} field="gasket_oring_spec" editData={editData} onChange={handleEditChange} />
                <div>
                  {isEditing ? (
                    <div className="flex items-center space-x-2 pt-6">
                      <Checkbox
                        id="gasket_food_grade"
                        checked={editData.gasket_food_grade || false}
                        onCheckedChange={(checked) => handleEditChange('gasket_food_grade', checked)}
                      />
                      <Label htmlFor="gasket_food_grade" className="cursor-pointer">Food Grade</Label>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-[#5A5A5A]">Food Grade</p>
                      <p className="font-medium text-[#323033]">{sr.gasket_food_grade ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#005EF8]" />
                Car Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoField label="Site Name" value={sr.site_name} editMode={isEditing} field="site_name" editData={editData} onChange={handleEditChange} />
                <InfoField label="SPLC Code" value={sr.splc_code} editMode={isEditing} field="splc_code" editData={editData} onChange={handleEditChange} />
                <InfoField label="Site Contact" value={sr.site_contact} editMode={isEditing} field="site_contact" editData={editData} onChange={handleEditChange} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Address" value={sr.location_address} editMode={isEditing} field="location_address" editData={editData} onChange={handleEditChange} />
                <InfoField label="Address Line 2" value={sr.location_address_2} editMode={isEditing} field="location_address_2" editData={editData} onChange={handleEditChange} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoField label="City" value={sr.location_city} editMode={isEditing} field="location_city" editData={editData} onChange={handleEditChange} />
                <InfoField label="State" value={sr.location_state} editMode={isEditing} field="location_state" editData={editData} onChange={handleEditChange} />
                <InfoField label="ZIP" value={sr.location_zip} editMode={isEditing} field="location_zip" editData={editData} onChange={handleEditChange} />
                <InfoField label="Contact Phone" value={sr.site_contact_number} editMode={isEditing} field="site_contact_number" editData={editData} onChange={handleEditChange} />
              </div>
            </CardContent>
          </Card>

          {/* Customer Photos */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-[#005EF8]" />
                    Customer Photos
                  </CardTitle>
                  <CardDescription>Photos provided by the customer ({customerPhotos.length}/4)</CardDescription>
                </div>
                {canEdit && customerPhotos.length < 4 && (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="bg-[#005EF8] hover:bg-[#0047C2]"
                      data-testid="upload-photo-btn"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Photo
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {customerPhotos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {customerPhotos.map((photo) => (
                    <div key={photo.id} className="relative group" data-testid={`photo-${photo.id}`}>
                      <img
                        src={normalizeFileUrl(photo.file_url)}
                        alt={photo.file_name}
                        className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setEnlargedPhoto(photo)}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-white hover:bg-white/20"
                          onClick={() => setEnlargedPhoto(photo)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canEdit && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-white hover:bg-red-500/50"
                            onClick={() => handlePhotoDelete(photo.id)}
                            data-testid={`delete-photo-${photo.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-[#5A5A5A] mt-1 truncate">{photo.file_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Camera className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
                  <p className="text-[#5A5A5A]">No customer photos uploaded</p>
                  {canEdit && (
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()} 
                      className="mt-4"
                      disabled={uploadingPhoto}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photo
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Requestor Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#005EF8]" />
                Service Requestor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoField label="Name" value={sr.requestor_full_name} editMode={isEditing} field="requestor_full_name" editData={editData} onChange={handleEditChange} />
              <InfoField label="Company" value={sr.requestor_company} editMode={isEditing} field="requestor_company" editData={editData} onChange={handleEditChange} />
              <InfoField label="Phone" value={sr.requestor_phone} editMode={isEditing} field="requestor_phone" editData={editData} onChange={handleEditChange} icon={<Phone className="w-4 h-4" />} />
              <InfoField label="Email" value={sr.requestor_email} editMode={isEditing} field="requestor_email" editData={editData} onChange={handleEditChange} icon={<Mail className="w-4 h-4" />} />
            </CardContent>
          </Card>

          {/* Billing Party */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#005EF8]" />
                Billing Party
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoField label="Company" value={sr.billing_company} editMode={isEditing} field="billing_company" editData={editData} onChange={handleEditChange} />
              <InfoField label="Contact Name" value={sr.billing_contact_name} editMode={isEditing} field="billing_contact_name" editData={editData} onChange={handleEditChange} />
              <InfoField label="Contact Email" value={sr.billing_contact_email} editMode={isEditing} field="billing_contact_email" editData={editData} onChange={handleEditChange} icon={<Mail className="w-4 h-4" />} />
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#005EF8]" />
                Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoField label="Call Date" value={sr.call_date} />
              <InfoField label="Created" value={sr.created_at ? new Date(sr.created_at).toLocaleString() : '-'} />
              {/* 2026-07-05 Wave 3 #8: creator + updater first/last names.
                  Backend resolves user_id → name at read time (no backfill). */}
              <InfoField label="Creator" value={sr.created_by_name || '—'} data-testid="sr-creator-name" />
              <InfoField label="Updated" value={sr.updated_at ? new Date(sr.updated_at).toLocaleString() : '-'} />
              <InfoField label="Updated by" value={sr.updated_by_name || '—'} data-testid="sr-updated-by-name" />
            </CardContent>
          </Card>

          {/* Responding Facility — 2026-07-18 Phase 1 transfer feature.
              Sits directly under Dates. In edit mode the dropdown is only
              exposed to Admin/Manager; Operator sees a locked read-only
              value. Changing the facility triggers a confirmation modal. */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#005EF8]" />
                Responding Facility
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isEditing && canTransferFacility ? (
                <div>
                  <Label htmlFor="responding-facility-select">Responding Facility</Label>
                  <Select
                    value={editData.facility_id || ''}
                    onValueChange={(v) => handleEditChange('facility_id', v)}
                    disabled={linkedWsBlockingTransfer}
                  >
                    <SelectTrigger
                      id="responding-facility-select"
                      data-testid="responding-facility-select"
                      title={linkedWsBlockingTransfer
                        ? 'This Service Request cannot be transferred while linked work is In Process or On Hold. Please resolve the active work before transferring.'
                        : ''}
                    >
                      <SelectValue placeholder="Select facility" />
                    </SelectTrigger>
                    <SelectContent>
                      {facilities.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {linkedWsBlockingTransfer && (
                    <p
                      className="text-xs text-amber-700 mt-2 flex items-start gap-1"
                      data-testid="transfer-blocked-note"
                    >
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        Transfer disabled: linked work is <b>In Process</b> or
                        <b> On Hold</b>. Resolve the active work before
                        transferring.
                      </span>
                    </p>
                  )}
                  {!linkedWsBlockingTransfer && facilities.length <= 1 && (
                    <p className="text-xs text-gray-500 mt-1">
                      No other facilities available within your access scope.
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Current</div>
                    <div className="text-sm font-medium" data-testid="responding-facility-current">
                      {sr.facility_name || facilityNameById(sr.facility_id)}
                    </div>
                  </div>
                  {isEditing && !canTransferFacility && (
                    <span className="text-xs text-gray-500 italic" data-testid="responding-facility-locked">
                      Locked — only Admin/Manager can transfer
                    </span>
                  )}
                </div>
              )}

              {/* Last transfer summary (optional, only if this SR has ever
                  been transferred). Kept compact — the full audit lives in
                  the car file's Work Order History timeline. */}
              {sr.last_transfer && (
                <div className="border-t pt-3 text-xs text-gray-600 space-y-0.5" data-testid="last-transfer-summary">
                  <div>
                    <span className="text-gray-500">Last transfer: </span>
                    <span className="font-medium">
                      {new Date(sr.last_transfer.at).toLocaleString()}
                    </span>
                    {sr.last_transfer.by_user_name && (
                      <> by <span className="font-medium">{sr.last_transfer.by_user_name}</span></>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500">From </span>
                    <span className="font-medium">{sr.last_transfer.from_facility_name || '—'}</span>
                    <span className="text-gray-500"> to </span>
                    <span className="font-medium">{sr.last_transfer.to_facility_name || '—'}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transfer confirmation modal */}
          <AlertDialog open={showTransferConfirm} onOpenChange={(o) => { if (!o) handleCancelTransfer(); }}>
            <AlertDialogContent data-testid="transfer-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Responding Facility transfer</AlertDialogTitle>
                <AlertDialogDescription className="text-black text-sm space-y-2">
                  <span className="block">
                    &ldquo;Responding Facility&rdquo; has been changed from{' '}
                    <b>{facilityNameById(sr.facility_id)}</b> to{' '}
                    <b>{facilityNameById(pendingTransferFacilityId)}</b>.
                  </span>
                  <span className="block">
                    The service request will be transferred to the new
                    facility. Work history and car file data will transfer
                    as well, and the status will be changed to{' '}
                    <b>&ldquo;New&rdquo;</b>.
                  </span>
                  <span className="block">Do you want to continue?</span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="transfer-cancel-btn" onClick={handleCancelTransfer}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction data-testid="transfer-continue-btn" onClick={handleConfirmTransfer} disabled={saving}>
                  {saving ? 'Transferring…' : 'Continue'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Status History */}
          {auditLogs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-[#005EF8]" />
                  Status History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {auditLogs.slice(0, 10).map((log, index) => (
                    <div 
                      key={log.id || index} 
                      className="flex items-start gap-3 text-sm border-l-2 border-gray-200 pl-3 pb-3"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {log.old_status} → {log.new_status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {log.performed_by_name} • {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service Request?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this service request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-sr"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enlarged Photo Modal */}
      {enlargedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setEnlargedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={normalizeFileUrl(enlargedPhoto.file_url)}
              alt={enlargedPhoto.file_name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-2">{enlargedPhoto.file_name}</p>
            <Button
              variant="ghost"
              size="icon"
              className="absolute -top-2 -right-2 text-white bg-black/50 hover:bg-black/70"
              onClick={() => setEnlargedPhoto(null)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for displaying/editing info fields
const InfoField = ({ label, value, editMode, field, editData, onChange, icon, ...rest }) => {
  if (editMode && field && onChange) {
    return (
      <div {...rest}>
        <Label className="text-sm text-[#5A5A5A]">{label}</Label>
        <Input 
          value={editData[field] || ''} 
          onChange={(e) => onChange(field, e.target.value)}
          className="mt-1"
        />
      </div>
    );
  }
  
  return (
    <div {...rest}>
      <p className="text-sm text-[#5A5A5A]">{label}</p>
      <p className="font-medium text-[#323033] flex items-center gap-2">
        {icon}
        {value || '-'}
      </p>
    </div>
  );
};

export default ServiceRequestDetailPage;
